<script lang="ts">
  import { onMount } from "svelte";
  import BreadcrumbNavigation from "@retailer/svelte/BreadcrumbNavigation/BreadcrumbNavigation.svelte";
  import DistributorHeader from "@retailer/svelte/ProductLines/Index/DistributorHeader.svelte";
  import DiscountInformationCard from "@retailer/svelte/ProductLines/Index/DiscountInformationCard.svelte";
  import InventoryInformationCard from "@retailer/svelte/ProductLines/Index/InventoryInformationCard.svelte";
  import { InventoryInformationCardType } from "@retailer/svelte/ProductLines/Index/InventoryInformationCardType";
  import ProductLinesContainer from "@retailer/svelte/ProductLines/ProductLineContainer/ProductLinesContainer.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import { params } from "@roxi/routify";
  import type { Distributor } from "@shared/types/Distributor";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getStatsigClient } from "@modern/services/boot/statsig";

  let authService = getControlStateAuthenticationService();

  let statsigClient = getStatsigClient() || window["Statsig"];

  let experiment = statsigClient?.getExperiment("distributor_plp_transparency");

  // Get the parameter value - Statsig returns string "true"/"false", so use string fallback
  $: showDistributorPlpCards = $params.showDistributorCards
    ? $params.showDistributorCards == "true"
    : experiment?.get("show_distributor_plp_cards", "false") === "true";

  export let discoveryPanelRes;
  export let Cart;
  export let productIndexBannersRes;
  export let shoppableUnitRes = {};
  export let stateParams;

  let distributorOrTagHeader: boolean = false;
  let isDistributorPlp: boolean = false;
  $: {
    stateParams = stateParams;
    isDistributorPlp = stateParams.distributors?.length > 0;
    distributorOrTagHeader = isDistributorPlp || stateParams.tagId;
  }

  let distributor: Distributor;

  $: distributor = $DistributorsStore.find(d => d.id == $params.distributors?.[0]) || {};

  $: hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );
  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );

  $: outerBreadcrumbClasses = distributorOrTagHeader
    ? "fizz-inset-vertical-24 margin-container"
    : "fizz-surface-gray";
  $: innerBreadcrumbClasses = distributorOrTagHeader
    ? "fizz-flex-row fizz-stack-16"
    : "fizz-inset-vertical-24 margin-container";
  $: headerTestId = distributorOrTagHeader ? "distributor-tag-header" : "standard-plp-header";
</script>

<style>
  .blue-header {
    background: linear-gradient(#f2f7fb 65%, transparent 65%);
  }
  .curved-border {
    border-radius: 16px;
  }

  .curved-border :global(#distributor-info-header > div.fizz-bordered) {
    border-radius: 16px;
  }

  .cards-container {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
</style>

<PageWrapper let:mobileMode>
  <div class="fizz-styles">
    <div class:blue-header={isDistributorPlp && !showDistributorPlpCards}>
      <div class={outerBreadcrumbClasses} data-testid={headerTestId}>
        <div
          class={innerBreadcrumbClasses}
          class:fizz-stack-32={isDistributorPlp && $LayoutStore.isDesktop}
        >
          <BreadcrumbNavigation overridePageTitle={true} />
        </div>

        {#if isDistributorPlp}
          <div class:curved-border={showDistributorPlpCards}>
            <DistributorHeader {distributor} {authStatus} />
            {#if showDistributorPlpCards}
              <div class="cards-container">
                <InventoryInformationCard {distributor} />
                <DiscountInformationCard {distributor} />
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </div>

  {#if isDistributorPlp && hideAllDistributorInfo && authStatus != "valid"}
    <svelte:component this={authService?.plpCtaComponent} {authStatus} {authService} {mobileMode} />
  {:else}
    <ProductLinesContainer
      bind:productIndexBannersRes
      bind:shoppableUnitRes
      {mobileMode}
      {Cart}
      {stateParams}
      {discoveryPanelRes}
      includeExternalAds={true}
      dedupeOrganicResultsFromAds={false}
    />
  {/if}
</PageWrapper>
